import { create } from 'zustand';
import { connection } from '@/lib/api';
import { API_BASE } from '@/lib/config';
import { getToken } from '@/stores/authStore';

export interface ZoomConnection {
  id: string;
  accountId: string;
  accountName: string;
  scopes: string[];
  connectedAt: string;
  lastUsedAt?: string;
  credentialPurpose?: string;
}

export interface EnrichedAccount {
  id: string;
  accountId: string;
  accountName: string;
  authType: string;
  scopes: string[];
  connectedAt: string;
  lastUsedAt?: string;
  email?: string;
  displayName?: string;
  picUrl?: string;
  userType?: number;
  userTypeName?: string;
  roleName?: string;
  planUnitedType?: string;
  timezone?: string;
  company?: string;
  enrichmentStatus: 'ok' | 'token_expired' | 'error';
  enrichmentError?: string;
}

export interface ConnectionCapabilities {
  canRead: boolean;
  canWrite: boolean;
  readCredentialId?: string;
  writeCredentialId?: string;
}

interface ConnectionState {
  // Multi-account state
  connections: ZoomConnection[];
  activeAccountId: string | null;
  isConnected: boolean;
  isLoading: boolean;
  isValidating: boolean;
  error: string | null;

  // Enriched accounts
  enrichedAccounts: EnrichedAccount[];
  isLoadingEnriched: boolean;

  // Capabilities
  capabilities: ConnectionCapabilities | null;

  // Actions
  checkConnection: () => Promise<void>;
  fetchEnrichedAccounts: () => Promise<void>;
  fetchCapabilities: () => Promise<ConnectionCapabilities | null>;
  connect: (credentials: { accountId: string; clientId: string; clientSecret: string }) => Promise<boolean>;
  connectWithDefaults: () => Promise<boolean>;
  testConnection: (credentials: { accountId: string; clientId: string; clientSecret: string }) => Promise<{ success: boolean; accountName?: string; error?: string }>;
  disconnect: (accountId?: string) => Promise<void>;
  setActiveAccount: (accountId: string) => void;
  getActiveConnection: () => ZoomConnection | null;
  getZoomConnectUrl: (returnTo?: string, purpose?: 'reader' | 'writer') => Promise<string | null>;
  clearError: () => void;
}

export const useConnectionStore = create<ConnectionState>((set, get) => ({
  connections: [],
  activeAccountId: null,
  isConnected: false,
  isLoading: false,
  isValidating: false,
  error: null,
  enrichedAccounts: [],
  isLoadingEnriched: false,
  capabilities: null,

  fetchEnrichedAccounts: async () => {
    set({ isLoadingEnriched: true });
    try {
      const token = getToken();
      const res = await fetch(`${API_BASE}/connection/accounts/enriched`, {
        headers: {
          Authorization: token ? `Bearer ${token}` : '',
          'X-Tenant-ID': '',
        },
      });
      if (!res.ok) throw new Error(`Request failed: ${res.status}`);
      const data = await res.json() as { accounts: EnrichedAccount[] };
      set({ enrichedAccounts: data.accounts });
    } catch {
      set({ enrichedAccounts: [] });
    } finally {
      set({ isLoadingEnriched: false });
    }
  },

  fetchCapabilities: async () => {
    try {
      const token = getToken();
      const res = await fetch(`${API_BASE}/connection/capabilities`, {
        headers: {
          Authorization: token ? `Bearer ${token}` : '',
          'X-Tenant-ID': '',
        },
      });
      if (!res.ok) return null;
      const data = await res.json() as ConnectionCapabilities;
      set({ capabilities: data });
      return data;
    } catch {
      return null;
    }
  },

  checkConnection: async () => {
    set({ isLoading: true, error: null });
    try {
      // Get all connected accounts
      const listResponse = await connection.listAccounts();
      const connections = listResponse.connections as ZoomConnection[];

      // Get active connection status
      const statusResponse = await connection.getStatus();
      const activeAccountId = statusResponse.connection?.accountId ?? null;

      set({
        connections,
        activeAccountId,
        isConnected: connections.length > 0,
      });
    } catch {
      // Not connected is not an error
      set({ isConnected: false, connections: [], activeAccountId: null });
    } finally {
      set({ isLoading: false });
    }
  },

  connect: async (credentials) => {
    set({ isLoading: true, error: null });
    try {
      const response = await connection.connect(credentials);

      if (response.success && response.connection) {
        const newConnection = response.connection as ZoomConnection;
        const { connections } = get();

        // Check if this account already exists (update case)
        const existingIndex = connections.findIndex(c => c.accountId === newConnection.accountId);
        let updatedConnections: ZoomConnection[];

        if (existingIndex >= 0) {
          updatedConnections = [...connections];
          updatedConnections[existingIndex] = newConnection;
        } else {
          updatedConnections = [...connections, newConnection];
        }

        set({
          isConnected: true,
          connections: updatedConnections,
          activeAccountId: newConnection.accountId,
        });
        return true;
      } else {
        set({ error: response.error ?? 'Connection failed' });
        return false;
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Connection failed';
      set({ error: message });
      return false;
    } finally {
      set({ isLoading: false });
    }
  },

  connectWithDefaults: async () => {
    set({ isLoading: true, error: null });
    try {
      const response = await connection.connectWithDefaults();

      if (response.success && response.connection) {
        const newConnection = response.connection as ZoomConnection;
        const { connections } = get();

        // Check if this account already exists
        const existingIndex = connections.findIndex(c => c.accountId === newConnection.accountId);
        let updatedConnections: ZoomConnection[];

        if (existingIndex >= 0) {
          updatedConnections = [...connections];
          updatedConnections[existingIndex] = newConnection;
        } else {
          updatedConnections = [...connections, newConnection];
        }

        set({
          isConnected: true,
          connections: updatedConnections,
          activeAccountId: newConnection.accountId,
        });
        return true;
      } else {
        set({ error: response.error ?? 'Connection failed' });
        return false;
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Connection with default credentials failed';
      set({ error: message });
      return false;
    } finally {
      set({ isLoading: false });
    }
  },

  testConnection: async (credentials) => {
    set({ isValidating: true, error: null });
    try {
      const response = await connection.testConnection(credentials);
      return response;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Validation failed';
      return { success: false, error: message };
    } finally {
      set({ isValidating: false });
    }
  },

  disconnect: async (accountId?: string) => {
    const { connections, activeAccountId } = get();
    const targetAccountId = accountId ?? activeAccountId;

    if (!targetAccountId) {
      set({ error: 'No account to disconnect' });
      return;
    }

    set({ isLoading: true, error: null });
    try {
      await connection.disconnectAccount(targetAccountId);

      const updatedConnections = connections.filter(c => c.accountId !== targetAccountId);

      // If we disconnected the active account, select the next one by lastUsedAt
      let newActiveAccountId = activeAccountId;
      if (targetAccountId === activeAccountId) {
        if (updatedConnections.length > 0) {
          // Sort by lastUsedAt (most recent first) and pick the first
          const sorted = [...updatedConnections].sort((a, b) => {
            const aTime = a.lastUsedAt ? new Date(a.lastUsedAt).getTime() : 0;
            const bTime = b.lastUsedAt ? new Date(b.lastUsedAt).getTime() : 0;
            return bTime - aTime;
          });
          newActiveAccountId = sorted[0]?.accountId ?? null;
        } else {
          newActiveAccountId = null;
        }
      }

      set({
        connections: updatedConnections,
        activeAccountId: newActiveAccountId,
        isConnected: updatedConnections.length > 0,
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Disconnection failed';
      set({ error: message });
    } finally {
      set({ isLoading: false });
    }
  },

  setActiveAccount: (accountId: string) => {
    const { connections } = get();
    const exists = connections.some(c => c.accountId === accountId);
    if (exists) {
      set({ activeAccountId: accountId });
    }
  },

  getActiveConnection: () => {
    const { connections, activeAccountId } = get();
    if (!activeAccountId) return null;
    return connections.find(c => c.accountId === activeAccountId) ?? null;
  },

  getZoomConnectUrl: async (returnTo?: string, purpose?: 'reader' | 'writer') => {
    try {
      const token = getToken();
      const params = new URLSearchParams();
      if (returnTo) params.set('returnTo', returnTo);
      if (purpose) params.set('purpose', purpose);
      const qs = params.toString();
      const resp = await fetch(`${API_BASE}/connection/zoom/url${qs ? `?${qs}` : ''}`, {
        headers: {
          Authorization: token ? `Bearer ${token}` : '',
          'X-Tenant-ID': '',
        },
      });
      if (!resp.ok) {
        throw new Error('Failed to get Zoom connect URL');
      }
      const data = await resp.json() as { url: string };
      return data.url;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to get connect URL';
      set({ error: message });
      return null;
    }
  },

  clearError: () => set({ error: null }),
}));
